import type { ReactNode } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import clsx from 'clsx';
import premiumSearchImg from '@/assets/img/premium_search.png';
import premiumPdfImg from '@/assets/img/premium_pdf.png';
import premiumArchiveImg from '@/assets/img/premium_archive.png';
import premiumNotesImg from '@/assets/img/premium_notes.png';

/** `grid` = centered cards, image above text (Settings page).
 *  `rows` = image left, text right, one feature per row (upsell modal). */
type PremiumFeaturesLayout = 'grid' | 'rows';

interface PremiumFeaturesProps {
  layout?: PremiumFeaturesLayout;
}

interface PremiumFeature {
  img: string;
  alt: string;
  title: ReactNode;
  description: ReactNode;
}

// Function so the t`` alt text and <Trans> nodes evaluate at render time.
function getPremiumFeatures(): PremiumFeature[] {
  return [
    {
      img: premiumSearchImg,
      alt: t`Full-Text Search`,
      title: <Trans>Full-Text Search</Trans>,
      description: (
        <Trans>
          Search through all of the articles you've saved to Instapaper, even ones you've
          archived.
        </Trans>
      ),
    },
    {
      img: premiumPdfImg,
      alt: t`PDF Reader`,
      title: <Trans>PDF Reader</Trans>,
      description: (
        <Trans>Save PDFs from the web or upload your own to Instapaper.</Trans>
      ),
    },
    {
      img: premiumArchiveImg,
      alt: t`Permanent Archive`,
      title: <Trans>Permanent Archive</Trans>,
      description: (
        <Trans>
          Stores a permanent copy of your articles so they will always be available, even
          if they disappear from the Internet.
        </Trans>
      ),
    },
    {
      img: premiumNotesImg,
      alt: t`Unlimited Notes`,
      title: <Trans>Unlimited Notes</Trans>,
      description: <Trans>Never worry about the 5-note monthly limit again.</Trans>,
    },
  ];
}

function PremiumFeatures({ layout = 'grid' }: PremiumFeaturesProps) {
  const features = getPremiumFeatures();

  if (layout === 'rows') {
    return (
      <div className="premium_features flex flex-col gap-7 px-1 py-6">
        {features.map((feature, index) => (
          <div key={index} className="premium_feature flex items-start gap-4">
            <img
              src={feature.img}
              alt={feature.alt}
              className="h-11 w-11 flex-shrink-0"
            />
            <div className="text">
              <div className="title mb-0.5 text-base font-bold">{feature.title}</div>
              <div className="description text-copy-secondary text-sm">
                {feature.description}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="premium_features px-4 py-8">
      <div className="premium_row flex flex-wrap justify-center gap-4">
        {features.map((feature, index) => (
          <div
            key={index}
            className={clsx(
              'premium_feature flex max-w-[128px] flex-col items-center text-center',
            )}
          >
            <div className="premium_img mb-4">
              <img
                src={feature.img}
                alt={feature.alt}
                className="mx-auto h-[40px] w-[40px]"
              />
            </div>
            <div className="text">
              <div className="title mb-1 text-xs font-bold">{feature.title}</div>
              <div className="description text-xs">{feature.description}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default PremiumFeatures;
